import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from "@mui/material";
import { getFetchHandler } from "../../library/api";
import { useEffect, useRef, useState } from "react";
import "./formGenerator.css";
import { useTranslation } from "react-i18next";

const mt = 2;

const AutoCompleteInput = ({
                               field,
                               value,
                               values,
                               onChange,
                               error,
                               helperText,
                               disabled,
                               allowCustomOption = false,
                           }) => {
    const { t } = useTranslation();

    const [options, setOptions] = useState(field?.data || []);
    const [loading, setLoading] = useState(false);

    // Keep this controlled to avoid undefined issues in MUI internals
    const [inputValue, setInputValue] = useState("");

    const [selectedValue, setSelectedValue] = useState(null);
    const prevProvinceId = useRef();

    // Control dropdown open state (hide dropdown when input is empty if allowCustomOption)
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Dialog state
    const [open, toggleOpen] = useState(false);
    const [dialogValue, setDialogValue] = useState({
        id: 0,
        label: "",
    });

    useEffect(() => {
        if (value == null) {
            setSelectedValue(null);
            return;
        }

        // If we already have an object with id+label, use it directly
        if (typeof value === "object" && value.id && value.label) {
            setSelectedValue(value);
            return;
        }

        // Try to resolve by id from options (works for {id} or primitive id)
        const wantId = typeof value === "object" && value !== null ? value.id : value;

        if (wantId != null) {
            const match = (Array.isArray(options) ? options : []).find(
                (o) => String(o?.id) === String(wantId)
            );
            if (match) setSelectedValue(match);
        }
    }, [value, options]);

    const handleClose = () => {
        setDialogValue({ id: 0, label: "" });
        toggleOpen(false);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const trimmed = (dialogValue.label || "").trim();
        if (!trimmed) return;

        const newOption = {
            id: dialogValue.id || 0,
            label: trimmed,
            isCustomValue: true,
        };

        setOptions((prev) => {
            const safePrev = Array.isArray(prev) ? prev : [];
            const exists = safePrev.some(
                (o) => (o?.id && o.id === newOption.id) || o?.label === newOption.label
            );
            return exists ? safePrev : [...safePrev, newOption];
        });

        setSelectedValue(newOption);
        onChange(field.id, newOption);
        setInputValue(newOption.label);
        setDropdownOpen(false);
        handleClose();
    };

    const baseFilter = createFilterOptions({
        matchFrom: "any",
        stringify: (option) => option?.search || option?.label || "",
    });

    // Initial / refetch
    useEffect(() => {
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field?.fetch]);

    // Remote search (debounced)
    useEffect(() => {
        if (!field?.fetch) return;

        const term = (inputValue || "").trim();
        if (term === "") {
            fetchOptions("");
            return;
        }
        if (term.length < 2) return;

        const timeout = setTimeout(() => fetchOptions(term), 600);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, field?.fetch]);

    useEffect(() => {
        if (field?.fetch !== "cities") return;

        const currProvinceId = values?.province?.id ?? null;
        const prev = prevProvinceId.current;

        if (!prev) {
            setSelectedValue(values?.city ?? null);
            onChange(field.id, values?.city ?? null);
            prevProvinceId.current = currProvinceId;
            fetchOptions("");
            return;
        }

        if (currProvinceId !== prev) {
            setSelectedValue(null);
            onChange(field.id, null);
            fetchOptions("");
        }

        prevProvinceId.current = currProvinceId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.province]);

    const getProvinceCode = () => {
        const p = values?.province?.id;
        return p || (typeof p === "string" ? p : undefined);
    };

    const fetchOptions = async (searchTerm = "") => {
        setLoading(true);
        try {
            const fetchHandler = getFetchHandler(field?.fetch);
            if (fetchHandler) {
                if (field.fetch === "cities") {
                    const provinceCode = getProvinceCode();
                    if (!provinceCode) {
                        setOptions([]);
                        setLoading(false);
                        return;
                    }
                    const data = await fetchHandler(provinceCode, searchTerm);
                    setOptions(Array.isArray(data) ? data : []);
                } else {
                    const data = await fetchHandler(searchTerm);
                    setOptions(Array.isArray(data) ? data : []);
                }
            } else {
                console.warn(`No fetch handler found for "${field?.fetch}"`);
                setOptions(field?.data || []);
            }
        } catch (err) {
            console.error("Error fetching options:", err);
            setOptions(field?.data || []);
        } finally {
            setLoading(false);
        }
    };

    const hasTyped = (inputValue || "").trim().length > 0;

    return (
        <>
            <Autocomplete
                freeSolo={allowCustomOption}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                value={selectedValue ?? null}
                inputValue={inputValue ?? ""} // ✅ controlled; prevents undefined->length crashes
                loading={loading}
                disablePortal
                options={Array.isArray(options) ? options : []}
                // Hide dropdown until user types (only when allowCustomOption)
                open={allowCustomOption ? dropdownOpen && hasTyped : undefined}
                onOpen={() => {
                    if (!allowCustomOption) return;
                    if (hasTyped) setDropdownOpen(true);
                }}
                onClose={() => setDropdownOpen(false)}
                isOptionEqualToValue={(option, val) => {
                    if (!option || !val) return false;
                    if (option.id !== undefined && val.id !== undefined) return option.id === val.id;
                    return option.label === val.label;
                }}
                getOptionLabel={(option) => {
                    // Keep "Add ..." visible in the dropdown
                    if (option && option.__isAddOption) return option.label || "";
                    if (typeof option === "string") return option;
                    if (option?.inputValue) return option.inputValue; // used when MUI creates option objects
                    return option?.label || "";
                }}
                filterOptions={(opts, params) => {
                    const safeOpts = Array.isArray(opts) ? opts : [];
                    const filtered = baseFilter(safeOpts, params);

                    if (!allowCustomOption) return filtered;

                    const iv = (params?.inputValue || "").trim();
                    const isExisting = safeOpts.some((o) => (o?.label || "") === iv);

                    // ✅ KEEP: Add "..."
                    if (iv !== "" && !isExisting) {
                        filtered.push({
                            inputValue: iv,
                            label: `${t("general.btnCreate")} "${iv}"`,
                            __isAddOption: true,
                        });
                    }

                    return filtered;
                }}
                onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                        setDialogValue({ id: 0, label: newValue });
                        toggleOpen(true);
                        return;
                    }

                    if (newValue && newValue.__isAddOption) {
                        setDialogValue({ id: 0, label: newValue.inputValue });
                        toggleOpen(true);
                        return;
                    }

                    if (newValue) {
                        setSelectedValue(newValue);
                        onChange(field.id, newValue);
                        setDropdownOpen(false);
                    } else {
                        setSelectedValue(null);
                        onChange(field.id, null);
                        setDropdownOpen(false);
                    }
                }}
                onInputChange={(event, newInputValue, reason) => {
                    const next = newInputValue ?? "";
                    setInputValue(next);

                    if (!allowCustomOption) return;

                    // Only open dropdown while the user is typing
                    if (reason === "input") {
                        setDropdownOpen(next.trim().length > 0);
                        return;
                    }

                    // When MUI resets input after selection (or clear), close dropdown
                    if (reason === "reset" || reason === "clear") {
                        setDropdownOpen(false);
                    }
                }}

                renderInput={(params) => (
                    <TextField
                        name={`field_${field.id}`}
                        placeholder={t(field.placeholder)}
                        className="textfield-outline"
                        {...params}
                        variant="outlined"
                        fullWidth
                        error={error}
                        helperText={helperText}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: "new-password", // disables autofill popup
                        }}
                    />
                )}
                disabled={disabled || (field.fetch === "cities" && !getProvinceCode())}
            />

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <form onSubmit={handleSubmit} className="tw-flex tw-flex-col">
                    <DialogTitle className="tw-font-semibold tw-text-lg">
                        {t("menu.company.addNew")}
                    </DialogTitle>

                    <DialogContent className="tw-space-y-4">
                        <DialogContentText className="tw-text-gray-600">
                            {t("menu.company.addCompanyName")}
                        </DialogContentText>

                        <TextField
                            className="textfield-outline"
                            margin="dense"
                            autoFocus
                            id="name"
                            value={dialogValue.label ?? ""}
                            onChange={(event) =>
                                setDialogValue((prev) => ({
                                    ...prev,
                                    label: event.target.value,
                                }))
                            }
                            type="text"
                            variant="outlined"
                            required
                            fullWidth
                        />
                    </DialogContent>

                    <DialogActions className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-pb-4">
                        <Button variant="contained" color="secondary" onClick={handleClose}>
                            {t("general.btnCancel")}
                        </Button>
                        <Button variant="contained" type="submit">
                            {t("general.btnSave")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};

export default AutoCompleteInput;
